import { useNavigationSettingsContext } from '@app/components/settings/navigation/NavigationSettingsContext';
import { Button } from '@app/components/various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { Filters } from '@app/components/various/Icons';
import { getMarkets } from '@app/ducks';
import { isNull } from '@app/utils/is';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  isDisabled?: boolean;
}

export const MarketSelector = ({ isDisabled = false }: Props) => {
  const { t } = useTranslation(['common', 'settings']);
  const markets = useSelector(getMarkets);

  const { selectedMarket, setSelectedMarket } = useNavigationSettingsContext();

  const handleChange = ([value]: string[]) => {
    if (value === 'allMarkets') {
      return setSelectedMarket(null);
    }

    setSelectedMarket(value ?? null);
  };

  const dropdownItems = [
    {
      key: 'allMarkets',
      label: t('settings:all_markets'),
    },
    ...markets.map(({ id, name }) => ({ key: id, label: name })),
  ];

  const selectedMarketLabel = isNull(selectedMarket) ? t('common:all') : markets.find(({ id }) => id === selectedMarket)?.name;

  return (
    <Dropdown title={t('common:market')} placement="bottom-end">
      <DropdownTrigger>
        {({ isOpen, triggerProps }) => (
          <Button
            aria-label={`${t('common:market')}: ${selectedMarketLabel}`}
            variant="bordered"
            color="secondary"
            size="medium"
            icon={Filters}
            disabled={isDisabled}
            isChevronOpen={isOpen}
            {...triggerProps}
          >
            {selectedMarketLabel}
          </Button>
        )}
      </DropdownTrigger>
      <DropdownMenu
        items={dropdownItems}
        selectedKeys={isNull(selectedMarket) ? ['allMarkets'] : [selectedMarket]}
        selectionMode="single"
        onSelectionChange={handleChange}
      />
    </Dropdown>
  );
};
